/**
 * API Constants
 * Single source of truth for API configuration
 */

// API Hosts
export const API_HOSTS = {
    DEVELOPMENT: 'http://localhost:4000',
    DEVELOPMENT_LOCAL: 'http://localhost:4000',
    PRODUCTION: 'https://api.dashboard.bottleshop.in',
} as const;

// Get the current host based on environment
export const getApiHost = (): string => {
    // Check VITE_ENV first, fallback to MODE
    const env = import.meta.env.VITE_ENV || import.meta.env.MODE;
    const isDevelopment = env === 'development';
    return isDevelopment ? API_HOSTS.DEVELOPMENT_LOCAL : API_HOSTS.PRODUCTION;
};

// API Configuration
export const API_CONFIG = {
    BASE_PATH: '/api/v1/admin',
    TIMEOUT: 30000,
    STOCK_OPS_TIMEOUT_MS: 120000,
    STOCK_OPS_MAX_INCLUSIVE_DAYS: 90,
} as const;

// Full Base URL - dynamically constructed based on environment
export const getApiBaseUrl = (): string => {
    return `${getApiHost()}${API_CONFIG.BASE_PATH}`;
};

// API Endpoints
export const API_ENDPOINTS = {
    // Authentication
    AUTH: {
        SEND_OTP: '/auth/send-otp',
        VERIFY_OTP: '/auth/verify-otp',
    },

    // Microsite Management
    MICROSITE: {
        ADMIN_DATA: (micrositeID: string) => `/microsite/admin-data/${micrositeID}`,
        SAVE: '/microsite/save',
        STATUS: '/microsite/status',
        LIST: '/microsite/list',
        STATS: '/microsite/stats',
        UPLOAD_URLS: '/microsite/upload-urls',
        SORTING_FILTERS: '/microsite/sorting-filters',
        SEARCH_STORE_BRANDS: '/microsite/search-store-brands',
        SEARCH_STORES: '/microsite/search-stores',
    },

    // Reports Admin (catalogue / templates / store brands)
    REPORT: {
        FILTER_OPTIONS: '/report/filter-options',
        CATALOG_LIST: '/report/catalog/list',
        CATALOG_DETAILS: '/report/catalog/details',
        CATALOG_CREATE: '/report/catalog/create',
        CATALOG_UPDATE: '/report/catalog/update',
        CATALOG_GENERATORS: '/report/catalog/generators',
        CATALOG_UPDATE_TEMPLATES: '/report/catalog/update-templates',
        CATALOG_UPDATE_STORE_BRANDS: '/report/catalog/update-store-brands',
        TEMPLATES_LIST: '/report/templates/list',
        TEMPLATES_DETAILS: '/report/templates/details',
        TEMPLATES_CREATE: '/report/templates/create',
        TEMPLATES_UPDATE: '/report/templates/update',
        TEMPLATES_TAGS: '/report/templates/tags',
        TEMPLATES_COPY_FROM_STORE_BRAND: '/report/templates/copy-from-store-brand',
        TEMPLATES_APPLY_TO_STORE_BRANDS: '/report/templates/apply-to-store-brands',
        STORE_BRANDS_LIST: '/report/store-brands/list',
        STORE_BRANDS_DETAILS: '/report/store-brands/details',
        STORE_BRANDS_UPDATE_REPORTS: '/report/store-brands/update-reports',
        STORE_BRANDS_NAME_OVERRIDE: '/report/store-brands/name-override',
    },

    STATE: {
        LIST: '/state/list',
    },

    // Blog Management
    BLOG: {
        SORTING_FILTERS: '/blog/sorting-filters',
        LIST: '/blog/list',
        DETAILS: '/blog/details',
        IMAGE_UPLOAD_URL: '/blog/image-upload-url',
        CREATE: '/blog/create',
        UPDATE: '/blog/update',
        STATUS: '/blog/status',
        DELETE: '/blog/delete',
    },

    // Store Management
    STORE_MANAGEMENT: {
        GET_STORES_BY_BRAND: (storeBrandId: string) => `/store/store-brands/${storeBrandId}/stores`,
        UPDATE_LIVE_STATUS: (storeId: string) => `/store/stores/${storeId}/store-status`,
        TRIGGER_STORE_EVENT: '/store-events/trigger-event',
        TRIGGER_ALL_USERS_EVENT: '/store-events/all-users/trigger-event',
        GET_UNSYNCED_ORDERS: '/store/unsynced-orders/list',
        DELETE_UNSYNCED_ORDERS: '/store/unsynced-orders/delete-action',
        UPDATE_UNSYNCED_ORDERS: '/store/unsynced-orders/update-action',
        GET_STORE_MEMBERS: '/store/members/list',
        GET_SALE_COMPLETION_WHATSAPP_CONFIG: (storeId: string) =>
            `/store/sale-completion-whatsapp-config/${storeId}`,
        UPDATE_SALE_COMPLETION_WHATSAPP_CONFIG: (storeId: string) =>
            `/store/sale-completion-whatsapp-config/${storeId}`,
        GET_MULTI_STORE_AGENT_CONFIG: (storeId: string) =>
            `/store/multi-store-agent-config/${storeId}`,
        UPDATE_MULTI_STORE_AGENT_CONFIG: (storeId: string) =>
            `/store/multi-store-agent-config/${storeId}`,
        // Brand chatbot lives outside /api/v1/admin — use full absolute URLs
        GET_BRAND_AGENT_CONFIG: () => `${getApiHost()}/api/v1/chatbot/get-multi-store-agent-config`,
        SAVE_BRAND_AGENT_CONFIG: () =>
            `${getApiHost()}/api/v1/chatbot/save-store-brand-multi-agent-config`,
        GET_LOGIN_POLICY: (storeBrandId: string) => `/store/login-policy/${storeBrandId}`,
        SAVE_LOGIN_POLICY: (storeBrandId: string) => `/store/login-policy/${storeBrandId}`,
        GET_DAILY_NOTIFICATION_SETTINGS: (storeBrandId: string) =>
            `/store/store-brand-settings/${storeBrandId}/daily-notification-settings`,
        UPDATE_DAILY_NOTIFICATION_SETTINGS: (storeBrandId: string) =>
            `/store/store-brand-settings/${storeBrandId}/daily-notification-settings`,
        GET_MOVEMENT_COUNT_CONFIG: (storeBrandId: string) =>
            `/store/store-brand-settings/${storeBrandId}/movement-count-config`,
        SAVE_MOVEMENT_COUNT_CONFIG: (storeBrandId: string) =>
            `/store/store-brand-settings/${storeBrandId}/movement-count-config`,
        REBUILD_CLOSING_STOCK: (storeId: string) =>
            `/store/stores/${storeId}/stock-ops/rebuild-closing-stock`,
        UPDATE_TODAY_STOCK_COUNT: (storeId: string) =>
            `/store/stores/${storeId}/stock-ops/update-today-stock-count`,
        GET_STORE_OPERATIONS_CONFIG: (storeId: string) =>
            `/store/stores/${storeId}/operations-config`,
        UPDATE_STORE_OPERATIONS_CONFIG: (storeId: string) =>
            `/store/stores/${storeId}/operations-config`,
        GET_BRAND_OPERATIONS_CONFIG: (storeBrandId: string) =>
            `/store/store-brand-settings/${storeBrandId}/operations-config`,
        UPDATE_BRAND_OPERATIONS_CONFIG: (storeBrandId: string) =>
            `/store/store-brand-settings/${storeBrandId}/operations-config`,
        GET_DASHBOARD_EXPIRY: (storeBrandId: string) =>
            `/store-brand/${storeBrandId}/dashboard-expiry`,
        UPDATE_DASHBOARD_EXPIRY: (storeBrandId: string) =>
            `/store-brand/${storeBrandId}/dashboard-expiry`,
    },

    // Central Product Master (admin)
    CENTRAL_PRODUCT_MASTER: {
        STATS: '/product-master/stats',
        BATCH_JOB_RETRY: (id: string) => `/product-master/batch-jobs/${id}/retry`,
        QUEUE_LIST: '/product-master/queue/list',
        QUEUE_COUNTS: '/product-master/queue/counts',
        QUEUE_STORE_BRAND_OPTIONS: '/product-master/queue/store-brand-options',
        QUEUE_APPROVE: '/product-master/queue/approve',
        QUEUE_BULK_APPROVE: '/product-master/queue/bulk-approve',
        QUEUE_BULK_RESOLVE: '/product-master/queue/bulk-resolve',
        QUEUE_RESOLVE: '/product-master/queue/resolve',
        QUEUE_DISMISS: '/product-master/queue/dismiss',
        QUEUE_NEXT_FLAGGED: '/product-master/queue/next-flagged',
        QUEUE_REJECT_PREVIEW: '/product-master/queue/reject-preview',
        QUEUE_REJECT: '/product-master/queue/reject',
        QUEUE_BARCODE_RESOLVE: '/product-master/queue/barcode/resolve',
        MERGE: '/product-master/merge',
        LIST: '/product-master/list',
        DETAILS: '/product-master/details',
        CREATE: '/product-master/create',
        UPDATE: '/product-master/update',
        DELETE: '/product-master/delete',
        BULK_UPSERT: '/product-master/bulk-upsert',
        VOLUME_SETTINGS: {
            LIST: '/product-master/volume-settings/list',
            CREATE: '/product-master/volume-settings/create',
            UPDATE: '/product-master/volume-settings/update',
            USAGE: '/product-master/volume-settings/usage',
            DELETE: '/product-master/volume-settings/delete',
        },
        EXCISE_CATEGORY: {
            LIST: '/excise-category/list',
            CREATE: '/excise-category/create',
            UPDATE: '/excise-category/update',
            STATUS: '/excise-category/status',
            DELETE: '/excise-category/delete',
            REFRESH_VOLUMES: '/excise-category/refresh-volumes',
        },
        STATE: {
            LIST: '/state/list',
            LIVE_STATUS: '/state/live-status',
            LICENSE_TYPES: '/state/license-types',
        },
    },

    CONFIG_TEMPLATE: {
        FIELD_SCHEMA: '/config-templates/field-schema',
        LIST: '/config-templates/list',
        TAGS: '/config-templates/tags',
        CREATE: '/config-templates/create',
        DETAILS: '/config-templates/details',
        UPDATE: '/config-templates/update',
        STATUS: '/config-templates/status',
        COPY_FROM_LIVE: '/config-templates/copy-from-live',
        OVERLAY_KEYS: '/config-templates/overlay-keys',
        STORE_BRANDS_LIST: '/config-templates/store-brands/list',
        STORES_LIST: '/config-templates/stores/list',
        APPLY: '/config-templates/apply',
    },

    // AI Purchase Analytics (see dashboard-backend/docs/ai-purchase-tracking-plan.md §5)
    AI_PURCHASE_ANALYTICS: {
        OVERALL: '/ai-purchase-analytics/overall',
        BY_STATE: '/ai-purchase-analytics/by-state',
        BY_STATE_BRANDS: (state: string) =>
            `/ai-purchase-analytics/by-state/${encodeURIComponent(state)}/brands`,
        BY_STATE_STORES: (state: string) =>
            `/ai-purchase-analytics/by-state/${encodeURIComponent(state)}/stores`,
        BY_STATE_BRAND_STORES: (state: string, brandId: string) =>
            `/ai-purchase-analytics/by-state/${encodeURIComponent(state)}/brands/${brandId}/stores`,
    },
} as const;

// AI Purchase Analytics — the 5 AI-enabled states, and default date-range length
export const AI_PURCHASE_ANALYTICS_STATES = [
    'Uttar Pradesh',
    'Assam',
    'Maharashtra',
    'Odisha',
    'Rajasthan',
] as const;

export const AI_PURCHASE_ANALYTICS_DEFAULT_DAYS = 15;

// Adoption-percentage badge thresholds (suggested by the plan doc, §7.3)
export const AI_PURCHASE_ADOPTION_THRESHOLDS = {
    LOW: 30,
    MEDIUM: 70,
} as const;

// Server-side search + pagination defaults for the by-state/:state/brands endpoint
// (matches the backend's skip/limit defaults — see ai-purchase-tracking-plan.md §5.2).
export const AI_PURCHASE_BRAND_LIST_DEFAULTS = {
    SKIP: 0,
    LIMIT: 10,
} as const;

// Debounce delay (ms) for the brand-breakdown search input before dispatching a fetch.
export const AI_PURCHASE_BRAND_SEARCH_DEBOUNCE_MS = 350;

// The brand page (page 3) needs the *full* per-state brand list (not the paginated
// grid's page) just to look up one brand's cumulative totals for its header — the
// brand count per state is always tens, not thousands, so the backend's max allowed
// limit (100, see ai-purchase-tracking-plan.md §5.2) is equivalent to "no pagination"
// for this specific lookup.
export const AI_PURCHASE_BRAND_HEADER_LOOKUP_LIMIT = 100;

// Brand list sort options (server-side via `sortValue` body field). Same labels are
// reused for the store-table local sort UI.
export const AI_PURCHASE_BRAND_SORT_OPTIONS = {
    ADOPTION_PURCHASES_LOW_TO_HIGH: 'Adoption by Purchases (Low to High)',
    ADOPTION_PURCHASES_HIGH_TO_LOW: 'Adoption by Purchases (High to Low)',
} as const;

export const AI_PURCHASE_BRAND_DEFAULT_SORT =
    AI_PURCHASE_BRAND_SORT_OPTIONS.ADOPTION_PURCHASES_LOW_TO_HIGH;

export const AI_PURCHASE_STORE_SORT_OPTIONS = AI_PURCHASE_BRAND_SORT_OPTIONS;
export const AI_PURCHASE_STORE_DEFAULT_SORT = AI_PURCHASE_BRAND_DEFAULT_SORT;

// Microsite Status Values (Backend API statuses)
export const MICROSITE_STATUS = {
    DRAFT: 'Draft',
    PUBLISHED: 'Published',
    INACTIVE: 'Inactive',
} as const;

// Image Upload Sections
export const IMAGE_SECTIONS = {
    LOGO: 'logo',
    OG_IMAGE: 'ogImage',
    HERO: 'hero',
    GALLERY: 'gallery',
    MODAL: 'modal',
} as const;

// Image Upload Limits
export const IMAGE_LIMITS = {
    [IMAGE_SECTIONS.LOGO]: 1,
    [IMAGE_SECTIONS.OG_IMAGE]: 1,
    [IMAGE_SECTIONS.HERO]: 10,
    [IMAGE_SECTIONS.GALLERY]: 20,
    [IMAGE_SECTIONS.MODAL]: 1,
} as const;

// Allowed Image Types
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

// Pagination Defaults
export const PAGINATION_DEFAULTS = {
    SKIP: 0,
    LIMIT: 20,
    MIN_LIMIT: 1,
    MAX_LIMIT: 100,
} as const;

// Sort Options (from API)
export const SORT_OPTIONS = {
    STORE_NAME_ASC: 'Store Name (A-Z)',
    STORE_NAME_DESC: 'Store Name (Z-A)',
    BRAND_NAME_ASC: 'Brand Name (A-Z)',
    BRAND_NAME_DESC: 'Brand Name (Z-A)',
    CREATED_AT_OLDEST: 'Created At (Oldest)',
    CREATED_AT_NEWEST: 'Created At (Newest)',
    UPDATED_AT_OLDEST: 'Updated At (Oldest)',
    UPDATED_AT_NEWEST: 'Updated At (Newest)',
} as const;

// Default Sort
export const DEFAULT_SORT = SORT_OPTIONS.CREATED_AT_NEWEST;

// Blog Status Values
export const BLOG_STATUS = {
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
} as const;

export type BlogStatus = (typeof BLOG_STATUS)[keyof typeof BLOG_STATUS];

// Blog Sort Options
export const BLOG_SORT_OPTIONS = {
    DATE_NEWEST: 'Date (Newest First)',
    DATE_OLDEST: 'Date (Oldest First)',
    TITLE_ASC: 'Title (A-Z)',
    TITLE_DESC: 'Title (Z-A)',
} as const;

export const BLOG_DEFAULT_SORT = BLOG_SORT_OPTIONS.DATE_NEWEST;

export const BLOG_ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

export const BLOG_MAX_IMAGE_SIZE_MB = 5;

// Day mapping for timings
export const DAYS_OF_WEEK = {
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
    7: 'Sunday',
} as const;

// Action types for save
export const MICROSITE_ACTIONS = {
    SAVE_DRAFT: 'saveDraft',
    PUBLISH: 'publish',
} as const;
